/**
 * Synapse Upload Analytics
 *
 * Provides analytics tracking for the Synapse async upload flow.
 * Can be used from Jotai atoms (non-React context) via the configured callback.
 *
 * Core events tracked:
 * - Session completion (success/failure with timing metrics)
 * - Chunk upload failures
 * - Preprocessing status (started/completed/failed)
 * - Chunk upload wait timeout
 */

// Event names (must match mixpanel.tsx definitions)
// Simplified to core events needed for measuring flow performance
export const SYNAPSE_EVENTS = {
  SESSION_COMPLETED: 'view.notes.synapse.session.completed',
  SESSION_COMPLETION_FAILED: 'view.notes.synapse.session.completion.failed',
  CHUNK_UPLOAD_FAILED: 'view.notes.synapse.chunk.upload.failed',
  PREPROCESSING_STARTED: 'view.notes.synapse.preprocessing.started',
  PREPROCESSING_COMPLETED: 'view.notes.synapse.preprocessing.completed',
  PREPROCESSING_FAILED: 'view.notes.synapse.preprocessing.failed',
} as const;

export type SynapseEventName =
  (typeof SYNAPSE_EVENTS)[keyof typeof SYNAPSE_EVENTS];

// Payload types for core events

export interface SessionCompletedPayload {
  ambientSessionId: string;
  opfsSessionId?: string;
  nucleusS3Key?: string;
  /** Total time from recording start to completion */
  totalDurationMs: number;
  chunkCount: number;
  /** Time spent in preprocessing/remux step */
  preprocessingDurationMs?: number;
}

export interface SessionCompletionFailedPayload {
  ambientSessionId: string;
  opfsSessionId?: string;
  error: string;
  errorCode?: string;
  stage: 'preprocessing' | 'completion' | 'unknown';
}

export interface ChunkUploadFailedPayload {
  sessionId: string;
  ambientSessionId?: string;
  chunkIndex: number;
  error: string;
  retryCount: number;
  isQuotaError: boolean;
}

export interface PreprocessingPayload {
  ambientSessionId: string;
  opfsSessionId?: string;
  chunkCount?: number;
  durationMs?: number;
  error?: string;
}

// Type for the tracking function
export type SynapseTrackFn = (
  eventName: SynapseEventName,
  payload: Record<string, unknown>,
) => void;

// Global tracking function - set by React component with access to mixpanel
let trackFn: SynapseTrackFn | null = null;

/**
 * Configure the analytics tracking function.
 * Called from React context (e.g., useSynapseUpload hook) to provide
 * access to mixpanel tracking.
 */
export function configureSynapseAnalytics(fn: SynapseTrackFn | null): void {
  trackFn = fn;
}

/**
 * Track a Synapse analytics event.
 * Safe to call even if tracking isn't configured (will no-op).
 */
export function trackSynapseEvent(
  eventName: SynapseEventName,
  payload: Record<string, unknown>,
): void {
  if (!trackFn) {
    // Silently ignore if tracking not configured
    return;
  }
  try {
    trackFn(eventName, {
      ...payload,
      timestamp: Date.now(),
    });
  } catch {
    // Don't let analytics errors break the app
  }
}

// Convenience functions for core events

export function trackSessionCompleted(payload: SessionCompletedPayload): void {
  trackSynapseEvent(SYNAPSE_EVENTS.SESSION_COMPLETED, { ...payload });
}

export function trackSessionCompletionFailed(
  payload: SessionCompletionFailedPayload,
): void {
  trackSynapseEvent(SYNAPSE_EVENTS.SESSION_COMPLETION_FAILED, { ...payload });
}

export function trackChunkUploadFailed(
  payload: ChunkUploadFailedPayload,
): void {
  trackSynapseEvent(SYNAPSE_EVENTS.CHUNK_UPLOAD_FAILED, { ...payload });
}

export function trackPreprocessingStarted(payload: PreprocessingPayload): void {
  trackSynapseEvent(SYNAPSE_EVENTS.PREPROCESSING_STARTED, { ...payload });
}

export function trackPreprocessingCompleted(
  payload: PreprocessingPayload,
): void {
  trackSynapseEvent(SYNAPSE_EVENTS.PREPROCESSING_COMPLETED, { ...payload });
}

export function trackPreprocessingFailed(payload: PreprocessingPayload): void {
  trackSynapseEvent(SYNAPSE_EVENTS.PREPROCESSING_FAILED, { ...payload });
}
